/**
 * Lupa 2× da folha (cara-crachá V1 + lupa, spec 2026-09-11 §7 item 1).
 *
 * A geometria é pura de propósito: é ela que decide onde a lente aparece e qual pedaço da
 * folha ela mostra, e é o único trecho da lupa que dá para testar sem DOM real.
 */

/** Dimensões aprovadas no mockup (painel V2 do `mockups.html`). */
export const MAGNIFIER_WIDTH = 220
export const MAGNIFIER_HEIGHT = 120
export const MAGNIFIER_ZOOM = 2

/** Chave por usuário no navegador — a preferência é local, não vai para o backend. */
export const MAGNIFIER_STORAGE_KEY = 'ds-attendance:audit:magnifier'

/** Texto do tooltip do botão quando a rotação bloqueia a lupa. */
export const MAGNIFIER_ROTATION_TOOLTIP = 'Lupa indisponível com rotação'

/**
 * `computeMagnifierGeometry` assume a folha na orientação original: com a imagem rotacionada o
 * recorte ampliado aponta para outra região da folha. Numa conferência de assinatura mostrar a
 * região errada é pior que não mostrar nada, então a lupa fica indisponível com rotação.
 *
 * Regra compartilhada pelo visualizador (que esconde a lente) e pela tela de auditoria (que
 * desabilita o botão) — os dois não podem discordar sobre quando a lupa funciona.
 */
export function isMagnifierBlockedByRotation(rotation: number | null | undefined): boolean {
  return typeof rotation === 'number' && Number.isFinite(rotation) && rotation % 360 !== 0
}

export interface MagnifierRect {
  left: number
  top: number
  width: number
  height: number
}

export interface MagnifierGeometryInput {
  /** Coordenadas de viewport do cursor (`MouseEvent.clientX/clientY`). */
  mouseX: number
  mouseY: number
  /** Retângulo de viewport do elemento renderizado (folha/canvas/imagem). */
  rect: MagnifierRect
  lensWidth?: number
  lensHeight?: number
  zoom?: number
}

export interface MagnifierGeometry {
  /** `false` quando o cursor saiu da folha — a lente não deve ser renderizada. */
  visible: boolean
  /** Posição da lente em px RELATIVOS ao retângulo (o overlay é filho dele). */
  lensLeft: number
  lensTop: number
  /** `background-size` da lente. */
  bgWidth: number
  bgHeight: number
  /** `background-position` da lente (negativa). */
  bgPosX: number
  bgPosY: number
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max)

/**
 * Lente centrada no cursor e presa dentro da folha: em nenhum momento ela mostra área fora
 * da folha, que na prática apareceria como um retângulo em branco no canto.
 *
 * O ponto sob o cursor fica no centro da lente — por isso `bgPos` é
 * `-(pontoAmpliado - metadeDaLente)`.
 */
export function computeMagnifierGeometry(input: MagnifierGeometryInput): MagnifierGeometry {
  const lensWidth = input.lensWidth ?? MAGNIFIER_WIDTH
  const lensHeight = input.lensHeight ?? MAGNIFIER_HEIGHT
  const zoom = input.zoom ?? MAGNIFIER_ZOOM

  const bgWidth = input.rect.width * zoom
  const bgHeight = input.rect.height * zoom

  const relX = input.mouseX - input.rect.left
  const relY = input.mouseY - input.rect.top

  const visible =
    input.rect.width > 0 &&
    input.rect.height > 0 &&
    relX >= 0 &&
    relY >= 0 &&
    relX <= input.rect.width &&
    relY <= input.rect.height

  // Lente maior que a folha (folha minúscula ou container ainda sem medida): a lente cola
  // no canto 0 em vez de assumir posição negativa.
  const maxLensLeft = Math.max(0, input.rect.width - lensWidth)
  const maxLensTop = Math.max(0, input.rect.height - lensHeight)

  return {
    visible,
    lensLeft: clamp(relX - lensWidth / 2, 0, maxLensLeft),
    lensTop: clamp(relY - lensHeight / 2, 0, maxLensTop),
    bgWidth,
    bgHeight,
    bgPosX: -(relX * zoom - lensWidth / 2),
    bgPosY: -(relY * zoom - lensHeight / 2)
  }
}

/**
 * Preferência da lupa. `localStorage` lança em navegador com armazenamento bloqueado e em
 * alguns modos privados — a lupa desligada é um default seguro, nunca um erro na tela.
 */
export function readMagnifierPreference(): boolean {
  try {
    return window.localStorage.getItem(MAGNIFIER_STORAGE_KEY) === 'on'
  } catch {
    return false
  }
}

export function writeMagnifierPreference(enabled: boolean): void {
  try {
    window.localStorage.setItem(MAGNIFIER_STORAGE_KEY, enabled ? 'on' : 'off')
  } catch {
    // Preferência é conveniência: não vale um toast nem um console.error a cada toggle.
  }
}
